/**
 * Sprint S-2 · #24 · Modal NPS in-app.
 *
 * Se muestra UNA vez al usuario (admin/gestor) cuando lleva ≥30 días desde
 * el registro. Si responde, no vuelve a aparecer en 365 días. Si pulsa "Más
 * tarde", se silencia 30 días.
 *
 * Render condicional: el componente consulta /api/feedback/nps/status al
 * montar; si should_show=false, no renderiza nada (return null).
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Sparkle, X, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

export function NpsModal() {
  const [open, setOpen] = useState(false);
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Mini-delay para no estorbar al primer render
    const t = setTimeout(() => {
      api.get("/feedback/nps/status")
        .then(({ data }) => { if (data?.should_show) setOpen(true); })
        .catch(() => {});
    }, 2500);
    return () => clearTimeout(t);
  }, []);

  const submit = async () => {
    if (score == null) return;
    setBusy(true);
    try {
      await api.post("/feedback/nps", { score, comment: comment.trim() || null });
      toast.success("¡Gracias por tu feedback!");
      setOpen(false);
    } catch {
      toast.error("No se pudo enviar. Reintenta más tarde.");
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    try { await api.post("/feedback/nps/dismiss"); } catch {}
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
      data-testid="nps-modal-overlay"
    >
      <div className="bg-white text-[#1A1A1A] w-full max-w-md rounded-md shadow-2xl overflow-hidden" data-testid="nps-modal">
        <div className="bg-[var(--ag-gold)] text-white px-5 py-4 flex items-start gap-3">
          <Sparkle size={20} weight="fill" className="text-amber-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Feedback rápido</div>
            <div className="font-display font-bold text-lg leading-tight mt-0.5">¿Recomendarías AutoGlow?</div>
          </div>
          <button onClick={dismiss} className="text-[var(--ag-text)]/80 hover:text-white p-1" data-testid="nps-modal-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-700">
            En una escala del <b>0 al 10</b>, ¿qué probabilidad hay de que recomiendes AutoGlow a otro profesional del motor?
          </p>
          <div className="grid grid-cols-11 gap-1.5" data-testid="nps-scale">
            {[0,1,2,3,4,5,6,7,8,9,10].map((n) => {
              const active = score === n;
              const color = n <= 6
                ? "border-red-300 hover:border-red-500"
                : n <= 8
                ? "border-amber-300 hover:border-amber-500"
                : "border-emerald-300 hover:border-emerald-500";
              const activeColor = n <= 6 ? "bg-red-500" : n <= 8 ? "bg-amber-500" : "bg-emerald-500";
              return (
                <button
                  key={n}
                  onClick={() => setScore(n)}
                  className={`h-10 rounded border ${active ? `${activeColor} text-[var(--ag-text)] border-transparent` : `${color} bg-white text-gray-700`} text-sm font-semibold transition-colors`}
                  data-testid={`nps-score-${n}`}
                >
                  {n}
                </button>
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] text-gray-500 -mt-1">
            <span>Nada probable</span><span>Muy probable</span>
          </div>
          {score != null && (
            <div>
              <label className="text-xs text-gray-600 uppercase tracking-wider">¿Por qué? <span className="opacity-50">(opcional)</span></label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                maxLength={600}
                placeholder={
                  score >= 9 ? "Lo que más te gusta…" :
                  score >= 7 ? "Qué podríamos mejorar…" :
                  "Cuéntanos qué falla — nos lo tomamos en serio"
                }
                className="w-full mt-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--ag-gold)]"
                data-testid="nps-comment"
              />
            </div>
          )}
          <div className="flex items-center justify-between gap-2 pt-1">
            <button onClick={dismiss} className="text-xs text-gray-500 hover:text-gray-700" data-testid="nps-later">
              Más tarde
            </button>
            <button
              onClick={submit}
              disabled={busy || score == null}
              className="inline-flex items-center gap-2 bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-white font-semibold px-4 py-2 rounded text-sm disabled:opacity-50"
              data-testid="nps-submit"
            >
              <PaperPlaneTilt size={14} weight="bold" /> Enviar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
